// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilValue } from 'recoil';

// Import Material UI Elements
import Button from '@material-ui/core/Button';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';

// Import Recoil
import { alertState, baseOrigin } from '../../../../../recoil-store/recoil-state';

// Import Icons
import DeleteIcon from '../../../../../assets/icons/trash-box'

// Import Services
import LibraryService from '../../../../../services/LibraryService/LibraryService';


// Import Pages
import ConfigurationForm from '../../../index'
import { Checkboxes } from '../../../../FlowConfigurationInputs';
import { TextInput } from "../../../../FlowConfigurationInputs/TextInput/NewTextInput";


const useStyles = makeStyles(theme => ({
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    },
    popupIndicator: {
        color: "#B0B0B0",
        height: "10px",
        width: "10px"
    }
}));

const types = [
    { value: "array", label: "Array" },
    { value: "boolean", label: "Boolean" },
    { value: "datatable", label: "Data Table" },
    { value: "date", label: "Date" },
    { value: "length", label: "Length" },
    { value: "number", label: "Number" },
    { value: "object", label: "Object" },
    { value: "string", label: "String" }
];

const operators = [
    { value: "gt", label: "GT", types: ["number", "date", "datatable", "length"] },
    { value: "ge", label: "GE", types: ["number", "date", "datatable", "length"] },
    { value: "eq", label: "EQ", types: ["string", "number", "date", "datatable", "boolean", "length"] },
    { value: "ne", label: "NE", types: ["string", "number", "date", "datatable", "boolean", "length"] },
    { value: "empty", label: "EMPTY", types: ["string"] },
    { value: "nempty", label: "NEMPTY", types: ["string"] },

    { value: "hop", label: "HOP", types: ["object"] },

    { value: "contains", label: "CONTAINS", types: ["string", "array"] },
    { value: "dncontains", label: "DNCONTAINS", types: ["string", "array"] },

    { value: "sw", label: "SW", types: ["string"] },
    { value: "ew", label: "EW", types: ["string"] },
    { value: "dnsw", label: "DNSW", types: ["string"] },
    { value: "dnew", label: "DNEW", types: ["string"] },
    { value: "lt", label: "LT", types: ["number", "date", "datatable", "length"] },
    { value: "le", label: "LE", types: ["number", "date", "datatable", "length"] }
];

function Condition({ conditionInfo, variables, setOpenVariable,
    index, conditionIndex, removeCondition, setManipulations }) {
    // setConditions yerine setManipulations yazılacak......

    const [selectedType, setSelectedType] = useState({});
    const [selectedOperator, setSelectedOperator] = useState({});

    const [firstValue, setFirstValue] = useState({ value: '' });
    const [secondValue, setSecondValue] = useState({ value: '' });

    const classes = useStyles();
    const label = "CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.DATA-TRANSFORM.";
    const { t } = useTranslation();

    const conditionLabel = 'CONTENT.CONFIGURATION-FORM.CONDITION.';


    useEffect(() => {
        if (conditionInfo) {
            let st = types.find(x => x.value === conditionInfo.condition);
            setSelectedType(st ? st : {});

            let so = operators.find(x => x.value === conditionInfo.operator);
            setSelectedOperator(so ? so : {});

            setFirstValue({ value: conditionInfo.fields.firstValue.value });
            setSecondValue({ value: conditionInfo.fields.secondValue.value });
        }
    }, [conditionInfo])

    const changeType = (v) => {

        setManipulations(prev => {
            let m = [...prev];

            m[index].conditions[conditionIndex].fields = { firstValue: { value: "" }, secondValue: { value: "" }, caseSensitive: { value: false } };
            m[index].conditions[conditionIndex].operator = "";
            m[index].conditions[conditionIndex].condition = v ? v.value : '';
            return m;
        });

        setFirstValue({ value: '' });
        setSecondValue({ value: '' });
        setSelectedOperator({});
        setSelectedType(v ? v : {});
    }

    const changeOperator = (v) => {
        setManipulations(prev => {
            let m = [...prev];

            m[index].conditions[conditionIndex].operator = v ? v.value : '';
            return m;
        });


        setSelectedOperator(v ? v : {});
    }

    const changeFirstValue = (v) => {
        setManipulations(prev => {
            let m = [...prev];

            m[index].conditions[conditionIndex].fields = { ...m[index].conditions[conditionIndex].fields, firstValue: { value: v } };
            return m;
        });

    }

    const changeSecondValue = (v) => {
        setManipulations(prev => {
            let m = [...prev];

            m[index].conditions[conditionIndex].fields = { ...m[index].conditions[conditionIndex].fields, secondValue: { value: v } };
            return m;
        });

    }

    const handleLogicChange = (e) => {
        let target = e.target;
        for (const el of target.parentNode.childNodes) {
            el.classList.remove('condition-active')
        }
        target.classList.add('condition-active')
        setManipulations(prev => {
            let m = [...prev];

            m[index].conditions[conditionIndex].logic = target.value;
            return m;
        });
    }

    return (
        <div className='m-t-20'>
            <div className='items-center'>
                <div className='condition-hr first'></div>
                <div className='condition-title'>{(t(label + "CONDITION")).toUpperCase() + `  #${conditionIndex + 1}`}</div>
                <div className='condition-hr second'></div>
                <DeleteIcon onClick={() => removeCondition(conditionIndex)} fill="#B0B0B0"
                    width={13} height={14} className="icon-cursor-pointer" />
            </div>

            <div className='t-a-condition-content'>
                <Autocomplete
                    value={selectedType}
                    onChange={(e, newValue) => changeType(newValue)}
                    id={`data-transform-condition-type-${conditionIndex}`}
                    className='custimize-text-area'
                    size="small"
                    options={types}
                    classes={{
                        option: classes.option,
                        inputRoot: classes.inputRoot,
                        popupIndicator: classes.popupIndicator
                    }}
                    autoHighlight
                    getOptionLabel={(option) => option?.label ? option.label : ""}
                    renderOption={(option) => (
                        option.label
                    )}
                    renderInput={(params) => (

                        <TextField
                            {...params}
                            id='condition-type-list-text'
                            className="select-action-input"
                            placeholder="Select Type"
                            variant="outlined"
                            classes={{
                                root: classes.inputRoot
                            }}
                            inputProps={{
                                ...params.inputProps,
                                "aria-autocomplete": 'new-password',
                                autoComplete: 'new-password', // disable autocomplete and autofill
                                style: {
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#46474E'
                                }
                            }}
                        />
                    )}
                />
                {
                    selectedType.value &&
                    <div>
                        <div className='m-t-8'>
                            <TextInput
                                className="custimize-text-area"
                                action={firstValue}
                                mockInputName={`firstValue_` + conditionIndex}
                                variables={variables}
                                setOpenVariable={setOpenVariable}
                                isRequired={false}
                                ignoreSave={true}
                                setField={changeFirstValue}
                                placeHolder={t(label + "SOURCE-VALUE")}
                            />
                        </div>

                        <Autocomplete
                            value={selectedOperator}
                            onChange={(e, newValue) => changeOperator(newValue)}
                            id={`data-transform-condition-operator-${conditionIndex}`}
                            className='custimize-text-area m-t-8'
                            size="small"
                            options={operators.filter(x => x.types.includes(selectedType.value))}
                            classes={{
                                option: classes.option,
                                inputRoot: classes.inputRoot,
                                popupIndicator: classes.popupIndicator
                            }}
                            autoHighlight
                            getOptionLabel={(option) => option?.label ? t(conditionLabel + option.label) : ""}
                            renderOption={(option) => (
                                t(conditionLabel + option.label)
                            )}
                            renderInput={(params) => (

                                <TextField
                                    {...params}
                                    id='operator-list-text'
                                    className="select-action-input"
                                    placeholder="Select Operator"
                                    variant="outlined"
                                    classes={{
                                        root: classes.inputRoot
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        "aria-autocomplete": 'new-password',
                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                        style: {
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#46474E'
                                        }
                                    }}
                                />

                            )}
                        />
                        {
                            selectedOperator.value && selectedOperator.value !== 'empty' && selectedOperator.value !== 'nempty' &&
                            <div className='m-t-8'>
                                <TextInput
                                    className="custimize-text-area"
                                    action={secondValue}
                                    mockInputName={`secondValue_` + conditionIndex}
                                    variables={variables}
                                    setOpenVariable={setOpenVariable}
                                    isRequired={false}
                                    ignoreSave={true}
                                    setField={changeSecondValue}
                                    placeHolder={t(label + "TARGET-VALUE")}
                                />
                            </div>
                        }

                        {conditionInfo.logic !== 'last' &&
                            <div className="form-element" style={{ textAlign: 'center' }}>
                                <div className="condition-tabs">
                                    <button className={conditionInfo.logic === 'and' ? 'active-logic' : ''}
                                        value="and" onClick={(e) => { handleLogicChange(e) }}>
                                        And
                                    </button>
                                    <button className={conditionInfo.logic === 'or' ? 'active-logic' : ''}
                                        value="or" onClick={(e) => { handleLogicChange(e) }}>
                                        Or
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                }


            </div>
        </div>
    )
}

export default Condition;